import { computed, toValue } from 'vue'
import { useUserExtendedData } from './useUserExtendedData'
import useAuthStore, { isUserToken } from '@/stores/authStore'
import { useSwaggerPaths, useResourceSwaggerDefinition, getPropertySchema, isReferenceObject } from './useSwagger'
import type { PathsWithMethod, HttpMethod } from 'openapi-typescript-helpers'
import type { Resource, allPaths } from '@/types/_general'
import type { UserExtendedData } from '@/types/user--extended-data'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import type { SwaggerPaths } from '@/types/swagger-paths'

// Cache for path lookups: maps requested path to actual access_rights key
const accessRightPathLookupCache = new WeakMap<AccessRights, Map<string, keyof AccessRights>>()
const swaggerPathLookupCache = new Map<string, keyof SwaggerPaths>()

type AccessRights = NonNullable<UserExtendedData['access_rights']>
type UserExtendedDataQuery = ReturnType<typeof useUserExtendedData>
type AuthStore = ReturnType<typeof useAuthStore>

/**
 * Resolves a path to the corresponding key in access_rights.
 * Handles the case where the TypeScript types use {resource_id} but access_rights uses the original parameter name.
 *
 * @param path - The path to look up (without leading slash), e.g., "cases/{resource_id}"
 * @param accessRights - The access_rights object from user extended data
 * @returns The matching key in access_rights, or null if not found
 */
function resolveAccessRightsPath(path: string, accessRights: AccessRights | undefined): keyof AccessRights | null {
  if (accessRights == null) return null

  // Get or create cache for this specific accessRights object
  let pathCache = accessRightPathLookupCache.get(accessRights)
  if (pathCache == null) {
    pathCache = new Map()
    accessRightPathLookupCache.set(accessRights, pathCache)
  }

  // Check cache first (scoped to this user's access_rights)
  const cached = pathCache.get(path)
  if (cached != null) return cached

  // Direct access - path exists as-is
  if (Object.hasOwn(accessRights, path)) {
    pathCache.set(path, path)
    return path
  }

  // Check if path has the form "resource/{resource_id}" and find a matching key
  const match = /^([^/]+)\/\{resource_id\}$/.exec(path)
  if (match?.[1]) {
    const resourceSegment = match[1]
    // Look for a key that starts with the same resource and has a path parameter
    const pattern = new RegExp(`^${resourceSegment}/\\{[^}]+\\}$`)
    for (const key of Object.keys(accessRights)) {
      if (pattern.test(key)) {
        pathCache.set(path, key)
        return key
      }
    }

    // Or, if the resource exists in the access rights, that also applies to
    // items with path params
    if (Object.hasOwn(accessRights, match[1])) {
      pathCache.set(path, match[1])
      return match[1]
    }
  }

  // No match found
  return null
}

function resolveSwaggerPath(_path: string, swaggerPaths: SwaggerPaths | undefined): keyof SwaggerPaths | null {
  if (swaggerPaths == null) return null
  const path = _path.startsWith('/') ? _path : `/${_path}`

  const cached = swaggerPathLookupCache.get(path)
  if (cached != null) return cached

  if (Object.hasOwn(swaggerPaths, path)) {
    swaggerPathLookupCache.set(path, path as keyof SwaggerPaths)
    return path as keyof SwaggerPaths
  }

  const match = /^\/([^/]+)\/\{resource_id\}$/.exec(path)
  if (match?.[1]) {
    const resourceSegment = match[1]
    // Look for a key that starts with the same resource and has a path parameter
    const pattern = new RegExp(`^/${resourceSegment}/\\{[^}]+\\}$`)
    for (const key of Object.keys(swaggerPaths)) {
      if (pattern.test(key)) {
        swaggerPathLookupCache.set(path, key as keyof SwaggerPaths)
        return key as keyof SwaggerPaths
      }
    }
  }

  // No match found
  return null
}

function isUserExtendedDataCurrent(authStore: AuthStore, userExtendedData: UserExtendedDataQuery): boolean {
  const tokenPayload = authStore.selectedToken?.payload
  if (!isUserToken(tokenPayload)) return false
  const data = userExtendedData.data.value
  if (data == null) return false
  return data._id === tokenPayload.u_id
}

function getResourceAccessContext(
  resource: MaybeRefOrGetter<Resource>,
  authStore: AuthStore,
  userExtendedData: UserExtendedDataQuery
) {
  if (userExtendedData.isError.value) return null
  const data = userExtendedData.data.value
  if (data == null) return null
  if (!isUserExtendedDataCurrent(authStore, userExtendedData)) return null

  const resourceValue = toValue(resource)
  const rights = data.access_rights?.[resourceValue]
  if (rights == null) return null

  return rights
}

function getAttributeAccessContext(
  resource: MaybeRefOrGetter<Resource>,
  attribute: MaybeRefOrGetter<string>,
  authStore: AuthStore,
  userExtendedData: UserExtendedDataQuery
) {
  const rights = getResourceAccessContext(resource, authStore, userExtendedData)
  if (rights == null) return null

  const attributeValue = toValue(attribute)
  const rootAttribute = attributeValue.split('.')[0]
  if (rootAttribute == null) return null

  return { rights, rootAttribute }
}

export function useAccess() {
  const userExtendedData = useUserExtendedData()
  const authStore = useAuthStore()
  const swaggerPaths = useSwaggerPaths()
  const accessGroupsSwaggerSchema = useResourceSwaggerDefinition('access--groups')

  /**
   * Async version - for event handlers, one-time checks, and batch operations.
   * For reactive use cases (query `enabled`, templates, computed), use the reactive version instead.
   */
  async function canAccessResourceAsync(resource: Resource) {
    try {
      await userExtendedData.suspense()
    } catch {
      return false
    }

    return getResourceAccessContext(resource, authStore, userExtendedData) != null
  }

  /**
   * Async version - for event handlers, one-time checks, and batch operations.
   * For reactive use cases (query `enabled`, templates, computed), use the reactive version instead.
   */
  async function canReadAttributeAsync(resource: Resource, attribute: string) {
    try {
      await userExtendedData.suspense()
    } catch {
      return false
    }

    const ctx = getAttributeAccessContext(resource, attribute, authStore, userExtendedData)
    if (ctx == null) return false

    const { rights, rootAttribute } = ctx
    if (rights.full_attribute_access === true) return true
    if (['read', 'write'].includes(rights.attribute_access?.[rootAttribute] as string)) return true

    return rootAttribute.startsWith('_')
  }

  /**
   * Async version - for event handlers, one-time checks, and batch operations.
   * For reactive use cases (query `enabled`, templates, computed), use the reactive version instead.
   */
  async function canWriteAttributeAsync(resource: Resource, attribute: string) {
    try {
      await userExtendedData.suspense()
    } catch {
      return false
    }

    const ctx = getAttributeAccessContext(resource, attribute, authStore, userExtendedData)
    if (ctx == null) return false

    const { rights, rootAttribute } = ctx
    if (rights.full_attribute_access === true) return true
    return rights.attribute_access?.[rootAttribute] === 'write'
  }

  /**
   * Async version - for event handlers, one-time checks, and batch operations.
   * For reactive use cases (query `enabled`, templates, computed), use the reactive version instead.
   */
  async function canCallMethodAsync<T extends HttpMethod>(
    method: T,
    // We have intellisense for methods that exist, but also allow arbitrary strings for dynamic paths,
    // as we may use this method to check if the method exists for the given path
    _path: PathsWithMethod<allPaths, T> | (string & {})
  ) {
    const path = _path.startsWith('/') ? _path.substring(1) : _path

    // Deny access if the requested method does not exist in the API according to the /swagger-paths endpoint
    try {
      await swaggerPaths.suspense()
    } catch {
      return false
    }
    const resolvedSwaggerPath = resolveSwaggerPath(path, swaggerPaths.data.value)
    if (resolvedSwaggerPath == null) return false
    if (!Object.hasOwn(swaggerPaths.data.value?.[resolvedSwaggerPath] ?? {}, method)) return false

    // We only allow
    if (isUserToken(authStore.selectedToken?.payload)) {
      // System Users has complete and full access to do everything!
      if (authStore.selectedToken.payload.system_user === true) return true

      // Readonly tokens cannot call non-GET methods
      if (authStore.selectedToken.payload.readonly === true && method !== 'get') return false
    }

    try {
      await userExtendedData.suspense()
    } catch {
      return false
    }
    if (userExtendedData.isError.value) return false
    if (!isUserExtendedDataCurrent(authStore, userExtendedData)) return false

    const accessRights = userExtendedData.data.value?.access_rights
    const resolvedPath = resolveAccessRightsPath(path, accessRights)
    if (resolvedPath == null) return false

    if (method === 'post' || method === 'patch') {
      try {
        await accessGroupsSwaggerSchema.suspense()

        if (accessGroupsSwaggerSchema.data.value == null) return false
        const resourceAccessSchema = getPropertySchema(
          accessGroupsSwaggerSchema.data.value,
          `access_rights.${resolvedPath}`
        )

        if (isReferenceObject(resourceAccessSchema)) return false
        const resourceHasFiltering =
          resourceAccessSchema.type === 'object' &&
          Object.hasOwn(resourceAccessSchema.properties ?? {}, 'full_filter_access')
        if (resourceHasFiltering) {
          if (!hasValidFilter(accessRights?.[resolvedPath] ?? {})) {
            return false
          }
        }
      } catch {
        return false
      }
    }

    // We only allow PUT if the user has full attribute access,
    // everyone who doesn't should use PATCH.
    if (method === 'put') {
      if (!accessRights?.[resolvedPath]?.full_attribute_access) return false
    }

    // And finally, we only allow the method if the user's access rights for the path
    // includes the requested method
    return (
      accessRights?.[resolvedPath]?.methods?.includes(
        method.toUpperCase() as NonNullable<AccessRights[keyof AccessRights]['methods']>[number]
      ) ?? false
    )
  }

  function hasValidFilter(accessRight: AccessRights[keyof AccessRights]) {
    if (accessRight.full_filter_access) return true

    if (accessRight.filter != null) {
      for (const filterValue of Object.values(accessRight.filter)) {
        if (Array.isArray(filterValue) && filterValue.length > 0) {
          return true
        }
      }
    }

    if (accessRight.user_filter != null) {
      for (const filterValue of Object.values(accessRight.user_filter)) {
        if (filterValue) {
          return true
        }
      }
    }

    return false
  }

  // ============================================
  // REACTIVE ACCESS FUNCTIONS (Primary API)
  // Use these for query `enabled` conditions,
  // templates (v-if/v-show), computed properties,
  // and any reactive context that needs to track
  // permission changes.
  // ============================================

  /**
   * Reactive check if user can access a resource.
   * Returns a computed ref that automatically updates when permissions change.
   * Use this for query `enabled` conditions, templates, and reactive state.
   */
  function canAccessResource(resource: MaybeRefOrGetter<Resource>): ComputedRef<boolean> {
    return computed(() => {
      return getResourceAccessContext(resource, authStore, userExtendedData) != null
    })
  }

  /**
   * Reactive check if user can read an attribute on a resource.
   * Returns a computed ref that automatically updates when permissions change.
   * Use this for query `enabled` conditions, templates, and reactive state.
   */
  function canReadAttribute(
    resource: MaybeRefOrGetter<Resource>,
    attribute: MaybeRefOrGetter<string>
  ): ComputedRef<boolean> {
    return computed(() => {
      const ctx = getAttributeAccessContext(resource, attribute, authStore, userExtendedData)
      if (ctx == null) return false

      const { rights, rootAttribute } = ctx
      if (rights.full_attribute_access === true) return true
      if (['read', 'write'].includes(rights.attribute_access?.[rootAttribute] as string)) return true

      return rootAttribute.startsWith('_')
    })
  }

  /**
   * Reactive check if user can write an attribute on a resource.
   * Returns a computed ref that automatically updates when permissions change.
   * Use this for query `enabled` conditions, templates, and reactive state.
   */
  function canWriteAttribute(
    resource: MaybeRefOrGetter<Resource>,
    attribute: MaybeRefOrGetter<string>
  ): ComputedRef<boolean> {
    return computed(() => {
      const ctx = getAttributeAccessContext(resource, attribute, authStore, userExtendedData)
      if (ctx == null) return false

      const { rights, rootAttribute } = ctx
      if (rights.full_attribute_access === true) return true

      return rights.attribute_access?.[rootAttribute] === 'write'
    })
  }

  /**
   * Reactive check if user can call a specific HTTP method on a path.
   * Returns a computed ref that automatically updates when permissions change.
   * Use this for query `enabled` conditions, templates, and reactive state.
   */
  function canCallMethod<T extends HttpMethod>(
    method: MaybeRefOrGetter<T>,
    _path: MaybeRefOrGetter<PathsWithMethod<allPaths, T> | (string & {})>
  ): ComputedRef<boolean> {
    return computed(() => {
      const methodValue = toValue(method)
      const pathValue = toValue(_path)
      const path = pathValue.startsWith('/') ? pathValue.substring(1) : pathValue

      if (swaggerPaths.isError.value) return false
      const swaggerPathsData = swaggerPaths.data.value
      if (swaggerPathsData == null) return false

      const resolvedSwaggerPath = resolveSwaggerPath(path, swaggerPathsData)
      if (resolvedSwaggerPath == null) return false
      if (!Object.hasOwn(swaggerPathsData[resolvedSwaggerPath], methodValue)) return false

      if (isUserToken(authStore.selectedToken?.payload)) {
        if (authStore.selectedToken.payload.system_user === true) return true
        if (authStore.selectedToken.payload.readonly === true && methodValue !== 'get') return false
      }

      if (userExtendedData.isError.value) return false
      const userData = userExtendedData.data.value
      if (userData == null) return false
      if (!isUserExtendedDataCurrent(authStore, userExtendedData)) return false

      const accessRights = userData.access_rights
      const resolvedPath = resolveAccessRightsPath(path, accessRights)
      if (resolvedPath == null) return false

      if (methodValue === 'post' || methodValue === 'patch') {
        if (accessGroupsSwaggerSchema.isError.value) return false
        const schemaData = accessGroupsSwaggerSchema.data.value
        if (schemaData == null) return false

        try {
          const resourceAccessSchema = getPropertySchema(schemaData, `access_rights.${resolvedPath}`)

          if (isReferenceObject(resourceAccessSchema)) return false
          const resourceHasFiltering =
            resourceAccessSchema.type === 'object' &&
            Object.hasOwn(resourceAccessSchema.properties ?? {}, 'full_filter_access')
          if (resourceHasFiltering) {
            if (!hasValidFilter(accessRights?.[resolvedPath] ?? {})) {
              return false
            }
          }
        } catch {
          return false
        }
      }

      if (methodValue === 'put') {
        if (accessRights?.[resolvedPath]?.full_attribute_access !== true) return false
      }

      return (
        accessRights?.[resolvedPath]?.methods?.includes(
          methodValue.toUpperCase() as NonNullable<AccessRights[keyof AccessRights]['methods']>[number]
        ) ?? false
      )
    })
  }

  /**
   * Reactive check that ONE access group holds PATCH or PUT on the resource together with write on
   * the attribute (or full attribute access) — the api's per-group write rule. The merged view
   * cannot answer it: a GET-only read-everything group makes `canWriteAttribute` true for users who
   * cannot write at all. A system user holds no groups and gets synthesised rights, so the merged
   * view is then read as its single group. Filters are not considered.
   */
  function canWriteAttributeInOneGroup(
    resource: MaybeRefOrGetter<Resource>,
    attribute: MaybeRefOrGetter<string>
  ): ComputedRef<boolean> {
    return computed(() => {
      if (userExtendedData.isError.value) return false
      const data = userExtendedData.data.value
      if (data == null) return false
      if (!isUserExtendedDataCurrent(authStore, userExtendedData)) return false

      const resourceValue = toValue(resource)
      const rootAttribute = toValue(attribute).replace(/\..*$/, '')

      const groupRights =
        data.access_groups != null && data.access_groups.length > 0
          ? data.access_groups.map((group) => group.access_rights)
          : [data.access_rights]

      return groupRights.some((accessRights) => {
        const rights = accessRights?.[resourceValue]
        if (rights == null) return false
        if (!rights.methods?.some((method) => method === 'PATCH' || method === 'PUT')) return false
        if (rights.full_attribute_access === true) return true
        return rights.attribute_access?.[rootAttribute] === 'write'
      })
    })
  }

  /** Whether the checks above can answer: all return `false` while rights, swagger paths or the access--groups schema are in flight. A failed query counts as settled. */
  const isLoaded = computed(
    () =>
      (userExtendedData.data.value != null || userExtendedData.isError.value) &&
      (swaggerPaths.data.value != null || swaggerPaths.isError.value) &&
      (accessGroupsSwaggerSchema.data.value != null || accessGroupsSwaggerSchema.isError.value)
  )

  return {
    isLoaded,

    // Reactive functions (Primary API) - for query enabled, templates, reactive state
    canAccessResource,
    canReadAttribute,
    canWriteAttribute,
    canWriteAttributeInOneGroup,
    canCallMethod,

    // Async functions (Secondary API) - for event handlers, one-time checks
    canAccessResourceAsync,
    canReadAttributeAsync,
    canWriteAttributeAsync,
    canCallMethodAsync,
  }
}
